import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Field } from "@/components/site/FormControls";
import { adminApi, apiError } from "@/lib/api";
import { useAdminUser } from "@/lib/adminAuth";

const startGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/client-portal";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function PortalLogin() {
  const navigate = useNavigate();
  const [user] = useAdminUser();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", company: "", phone: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/client-portal"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = mode === "login"
        ? await adminApi.post("/auth/login", { email: f.email, password: f.password })
        : await adminApi.post("/auth/register", f);
      navigate(data.role === "admin" ? "/admin" : "/client-portal");
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#F5F3EC] px-4 py-16" data-testid="portal-login-page">
      <form onSubmit={submit} className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8" data-testid="portal-auth-form">
        <h1 className="font-display text-2xl font-bold text-[#0B132B]">Client Portal</h1>
        <p className="mt-1 text-sm text-slate-500">Track requests, appointments, reports and invoices.</p>
        <div className="mt-6 grid grid-cols-2 gap-1 rounded-full bg-[#F5F3EC] p-1">
          {[["login", "Sign in"], ["register", "Create account"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`portal-mode-${k}`}
              className={`rounded-full py-2 text-sm font-semibold ${mode === k ? "bg-white text-[#0B132B] shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-6 grid gap-4">
          {mode === "register" && (
            <>
              <Field label="Full name" name="name" value={f.name} onChange={set} required testid="portal-name" />
              <Field label="Company (optional)" name="company" value={f.company} onChange={set} testid="portal-company" />
              <Field label="Phone" name="phone" value={f.phone} onChange={set} testid="portal-phone" />
            </>
          )}
          <Field label="Email" name="email" type="email" value={f.email} onChange={set} required testid="portal-email" />
          <Field label="Password (min 8 characters)" name="password" type="password" value={f.password} onChange={set} required testid="portal-password" />
        </div>
        {err && <p data-testid="portal-auth-error" className="mt-4 text-sm text-red-600">{err}</p>}
        <button type="submit" disabled={loading} data-testid="portal-auth-submit"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#0B132B] py-3 text-sm font-semibold text-white hover:bg-gold hover:text-[#0B132B] disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} {mode === "login" ? "Sign in" : "Create account"}
        </button>
        <button type="button" onClick={startGoogle} data-testid="portal-google-login" className="mt-3 w-full rounded-full border border-slate-300 py-3 text-sm font-semibold text-[#0B132B] hover:border-gold">
          Continue with Google
        </button>
        <p className="mt-5 text-xs text-slate-400">Requests submitted while signed in appear in your portal automatically. Signing in with Google also shows requests made with that email.</p>
      </form>
    </section>
  );
}
